// Userflow.js onboarding integration (horAIzen onboarding flows).
//
// TOKEN SETUP — never hard-code the token; it comes from VITE_USERFLOW_TOKEN:
//   • Production:    set VITE_USERFLOW_TOKEN to the Userflow *Production* environment token
//                    in the hosting provider's env vars (AWS Amplify Console → prod branch).
//   • Staging / dev: set VITE_USERFLOW_TOKEN to the Userflow *Development* environment token
//                    (Amplify dev branch env vars, or a local `.env.local` for `npm run dev`).
//   • When USERFLOW_ENABLED is false (see env.js) or the token is absent, exports are no-ops.
//
// SPA navigation: Userflow.js watches history/URL changes itself, so client-side route
// changes (e.g. /properties → /property/{propertyId}) are tracked without extra wiring.

import { IS_DEV, USERFLOW_ENABLED, USERFLOW_TOKEN } from "@/utils/env";

let userflowPromise = null;
let readyPromise = null;
let deviceTypeListenerRegistered = false;
let sharedDealLensAnonymousIdentified = false;
let lastDeviceType = null;

// Keep in sync with AuthenticatedLayout mobile breakpoint (max-width: 1199px).
const MOBILE_MEDIA_QUERY = "(max-width: 1199px)";

function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

/**
 * @param {(userflow: import("userflow.js").Userflow) => void | Promise<void>} fn
 */
function whenUserflow(fn) {
  if (!USERFLOW_ENABLED || !isBrowser()) return;
  getReadyUserflow()
    .then((userflow) => {
      if (!userflow) return;
      return Promise.resolve(fn(userflow));
    })
    .catch((err) => {
      if (IS_DEV) console.warn("[userflow]", err);
    });
}

/**
 * @returns {Promise<import("userflow.js").Userflow | null>}
 */
function getReadyUserflow() {
  if (!USERFLOW_ENABLED || !USERFLOW_TOKEN || !isBrowser()) return Promise.resolve(null);
  if (!readyPromise) {
    if (!userflowPromise) {
      userflowPromise = import("userflow.js")
        .then((mod) => mod.default)
        .catch((err) => {
          if (IS_DEV) console.warn("[userflow] failed to load userflow.js", err);
          return null;
        });
    }
    readyPromise = userflowPromise.then((userflow) => {
      if (!userflow) return null;
      return Promise.resolve(userflow.init(USERFLOW_TOKEN))
        .then(() => userflow)
        .catch((err) => {
          if (IS_DEV) console.warn("[userflow] init failed", err);
          return null;
        });
    });
  }
  return readyPromise;
}

export function getUserflowDeviceType() {
  if (!isBrowser() || typeof window.matchMedia !== "function") return "desktop";
  return window.matchMedia(MOBILE_MEDIA_QUERY).matches ? "mobile" : "desktop";
}

function registerDeviceTypeListener() {
  if (deviceTypeListenerRegistered) return;
  if (!isBrowser() || typeof window.matchMedia !== "function") return;
  deviceTypeListenerRegistered = true;
  const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
  lastDeviceType = mediaQuery.matches ? "mobile" : "desktop";
  mediaQuery.addEventListener("change", (e) => {
    const deviceType = e.matches ? "mobile" : "desktop";
    if (deviceType === lastDeviceType) return;
    lastDeviceType = deviceType;
    whenUserflow((userflow) => userflow.updateUser({ device_type: deviceType }));
  });
}

export function initUserflow() {
  getReadyUserflow();
}

function buildSafeUserAttributes(authStore) {
  const profile = authStore.userProfile ?? {};
  const attributes = {};
  const name = String(authStore.fullName ?? "").trim();
  const email = String(authStore.getEffectiveEmail?.() ?? "").trim();
  const role = String(authStore.role ?? "").trim();
  const signedUpAt = String(profile.insert_date ?? profile.created_at ?? profile.date_created ?? "").trim();

  if (name && name !== "User") attributes.name = name;
  if (email) attributes.email = email;
  if (role) attributes.role = role;
  if (signedUpAt) attributes.signed_up_at = signedUpAt;
  attributes.device_type = getUserflowDeviceType();
  return attributes;
}

export function identifyUserflowFromAuth(authStore) {
  if (!USERFLOW_ENABLED || !authStore) return;
  const userId = String(authStore.userId ?? "").trim();
  if (!userId) return;

  registerDeviceTypeListener();
  whenUserflow((userflow) => {
    const attributes = buildSafeUserAttributes(authStore);
    lastDeviceType = attributes.device_type;
    return Promise.resolve(userflow.identify(userId, attributes)).then(() => {
      const companyId = String(authStore.customer ?? "").trim();
      if (!companyId) return;
      const account = Array.isArray(authStore.accounts)
        ? authStore.accounts.find((a) => String(a?.id ?? a?.name ?? "") === companyId)
        : null;
      const groupAttributes = {};
      const companyName = account ? String(authStore.getDisplayName?.(account) ?? "").trim() : "";
      const plan = String(account?.plan ?? "").trim();
      if (companyName && companyName !== "Unknown") groupAttributes.name = companyName;
      if (plan) groupAttributes.plan = plan;
      return userflow.group(companyId, groupAttributes);
    });
  });
}

const SHARED_DEALLENS_VIEW_PATH = "/deallens/view";

export function identifyAnonymousUserflowForSharedDealLens(authStore) {
  if (!USERFLOW_ENABLED || !isBrowser()) return;
  if (window.location.pathname.replace(/\/+$/, "") !== SHARED_DEALLENS_VIEW_PATH) return;
  if (String(authStore?.userId ?? "").trim()) return;
  if (sharedDealLensAnonymousIdentified) return;

  sharedDealLensAnonymousIdentified = true;
  getReadyUserflow()
    .then((userflow) => {
      if (!userflow) {
        sharedDealLensAnonymousIdentified = false;
        return;
      }
      return userflow.identifyAnonymous({
        auth_status: "anonymous",
        flow_audience: "possible_investor",
        shared_deallens_view: true,
        page_type: "shared_deallens_view",
      });
    })
    .catch((err) => {
      sharedDealLensAnonymousIdentified = false;
      if (IS_DEV) console.warn("[userflow]", err);
    });
}

export function resetUserflow() {
  sharedDealLensAnonymousIdentified = false;
  whenUserflow((userflow) => userflow.reset());
}
